import {
    Button,
    FormControl,
    IconButton,
    InputLabel,
    MenuItem,
    Select,
    Typography,
    withStyles,
} from '@material-ui/core';
import VisibilityIcon from '@material-ui/icons/Visibility';
import { AgGridColumn, AgGridReact } from 'ag-grid-react';
import { useEffect, useState } from 'react';
import { useHistory } from 'react-router';
import { axiosInstance } from '../../utils/axiosInstance';
import {
    endpoint,
    fwTaskControlCopy,
    fwTaskStatus,
    fwTaskStatusColors,
    fwTaskStatusLabels,
    fwTaskStatusValue,
    fwTaskTypeLabels,
    snackbarDefaultValue,
    snackbarVariant,
    tableTranslations,
} from '../../utils/constants';
import { formatFwDate } from '../../utils/fwHelpers';
import { consumeTaskRetirement } from '../../utils/fwTaskControlResult';
import LoadingSpinner from '../LoadingSpinner/LoadingSpinner';
import Snackbar from '../Snackbar/Snackbar';
import TitleHeader from '../TitleHeader/TitleHeader';

const ALL_STATUSES = 'all';

const TaskDetailsRenderer = ({ data, context }) => (
    <IconButton size="small" title="Szczegóły" onClick={() => context.onDetails(data)}>
        <VisibilityIcon fontSize="small" />
    </IconButton>
);

const styles = {
    root: {
        height: '100%',
        boxSizing: 'border-box',
        padding: '1rem 2rem',
        display: 'flex',
        flexDirection: 'column',
    },
    actions: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'flex-start',
        gap: '1rem',
        marginBottom: '1rem',
    },
    statusFilter: {
        minWidth: '11rem',
    },
    table: {
        fontFamily: 'inherit',
        width: '100%',
        flex: 1,
        minHeight: 0,
    },
    loadingWrapper: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: '100%',
        flex: 1,
    },
};

const FwTasks = ({ classes }) => {
    const history = useHistory();

    const [retiredTaskId, setRetiredTaskId] = useState(null);
    const [tasks, setTasks] = useState([]);
    const [statusFilter, setStatusFilter] = useState(ALL_STATUSES);
    const [isLoading, setIsLoading] = useState(true);
    const [snackbarData, setSnackbarData] = useState(snackbarDefaultValue);

    const showMessage = (message, variant) => {
        setSnackbarData({ isSnackbarOpen: true, message, variant, errorInfo: '' });
    };

    const handleIsSnackbarOpen = () => setSnackbarData(snackbarDefaultValue);

    const fetchTasks = () => {
        setIsLoading(true);
        axiosInstance
            .get(endpoint.FW_TASKS)
            .then(res => setTasks(res.data?.data ?? res.data ?? []))
            .catch(err =>
                showMessage(err.response?.data?.message || 'Błąd pobierania zadań', snackbarVariant.ERROR)
            )
            .finally(() => setIsLoading(false));
    };

    useEffect(() => {
        const taskId = consumeTaskRetirement(history);
        if (taskId) setRetiredTaskId(taskId);
        fetchTasks();
    }, []);

    const statusRenderer = params => {
        const status = fwTaskStatusValue(params.value);
        const color = fwTaskStatusColors[status] || '#000';
        return `<span style="color: ${color}">${fwTaskStatusLabels[status] || params.value}</span>`;
    };

    const handleDetails = data => history.push(`/fw-task/${data.id}`);

    const visibleTasks =
        statusFilter === ALL_STATUSES
            ? tasks
            : tasks.filter(task => String(fwTaskStatusValue(task.status)) === statusFilter);

    return (
        <div className={classes.root}>
            <TitleHeader title="Moduł FW — Zadania" />
            {retiredTaskId && <Typography role="status">{fwTaskControlCopy.retired}</Typography>}
            <div className={classes.actions}>
                <Button variant="contained" color="primary" onClick={() => history.push('/fw-task/create')}>
                    Dodaj zadanie
                </Button>
                <Button onClick={fetchTasks} variant="outlined">
                    Odśwież
                </Button>
                <FormControl variant="outlined" size="small" className={classes.statusFilter}>
                    <InputLabel id="fw-task-status-filter-label">Status zadania</InputLabel>
                    <Select
                        labelId="fw-task-status-filter-label"
                        id="fw-task-status-filter"
                        value={statusFilter}
                        onChange={event => setStatusFilter(event.target.value)}
                        label="Status zadania">
                        <MenuItem value={ALL_STATUSES}>Wszystkie</MenuItem>
                        {Object.entries(fwTaskStatus).map(([key, value]) => (
                            <MenuItem key={key} value={String(value)}>
                                {fwTaskStatusLabels[value]}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
            </div>
            {isLoading ? (
                <div className={classes.loadingWrapper}>
                    <LoadingSpinner />
                </div>
            ) : visibleTasks.length === 0 ? (
                <Typography>
                    {statusFilter === ALL_STATUSES ? 'Brak zadań' : 'Brak zadań dla wybranego statusu'}
                </Typography>
            ) : (
                <div className={'ag-theme-alpine'} style={styles.table}>
                    <AgGridReact
                        localeText={tableTranslations}
                        rowData={visibleTasks}
                        suppressCellSelection
                        pagination
                        paginationPageSize={50}
                        enableCellTextSelection
                        accentedSort
                        context={{ onDetails: handleDetails }}>
                        <AgGridColumn field="id" headerName="ID" width={60} sortable filter resizable />
                        {/* flex (not sizeColumnsToFit) keeps the grid filling the width across React re-renders */}
                        <AgGridColumn field="name" headerName="Nazwa" flex={1} sortable filter resizable />
                        <AgGridColumn
                            headerName="Plik .bin"
                            width={240}
                            valueGetter={params => params.data.fw_file?.filename || ''}
                            sortable
                            filter
                            resizable
                        />
                        <AgGridColumn
                            field="firmware_type"
                            headerName="Typ urządzenia"
                            width={130}
                            valueFormatter={params => fwTaskTypeLabels[params.value] || params.value}
                            sortable
                            filter
                        />
                        <AgGridColumn
                            field="status"
                            headerName="Status zadania"
                            width={150}
                            sortable
                            filter
                            cellRenderer={statusRenderer}
                        />
                        <AgGridColumn
                            field="devices_count"
                            headerName="Urządzenia"
                            width={110}
                            sortable
                            filter
                        />
                        <AgGridColumn
                            field="created_at"
                            headerName="Utworzono"
                            width={180}
                            valueFormatter={params => formatFwDate(params.value)}
                            sortable
                            filter
                        />
                        <AgGridColumn
                            headerName=""
                            width={80}
                            sortable={false}
                            filter={false}
                            cellRendererFramework={TaskDetailsRenderer}
                        />
                    </AgGridReact>
                </div>
            )}
            <Snackbar snackbarData={snackbarData} handleIsSnackbarOpen={handleIsSnackbarOpen} />
        </div>
    );
};

export default withStyles(styles)(FwTasks);
